import React from 'react';
import { FiCheck, FiX } from 'react-icons/fi';

const TOOLS_COMPARISON = [
  {
    tool: 'Notion',
    price: '10-18€/user',
    features: {
      'Bibliothèque de messages organisés': true,
      'Collaboration éditoriale': true,
      'Tâches et suivi': false,
      'Multi-tenant sécurisé natif': false,
      'Diffusion de messages structurée': false,
    },
    color: '#000000',
  },
  {
    tool: 'ClickUp',
    price: '7-12€/user',
    features: {
      'Bibliothèque de messages organisés': false,
      'Collaboration éditoriale': false,
      'Tâches et suivi': true,
      'Multi-tenant sécurisé natif': false,
      'Diffusion de messages structurée': false,
    },
    color: '#7b68ee',
  },
  {
    tool: 'Confluence',
    price: '5-10€/user',
    features: {
      'Bibliothèque de messages organisés': true,
      'Collaboration éditoriale': true,
      'Tâches et suivi': false,
      'Multi-tenant sécurisé natif': false,
      'Diffusion de messages structurée': false,
    },
    color: '#0052cc',
  },
  {
    tool: 'Outils de com',
    price: '5-10€/user',
    features: {
      'Bibliothèque de messages organisés': false,
      'Collaboration éditoriale': false,
      'Tâches et suivi': false,
      'Multi-tenant sécurisé natif': false,
      'Diffusion de messages structurée': true,
    },
    color: '#10b981',
  },
  {
    tool: 'PulseCIO (messagerie SaaS collaborative)',
    price: '8-25€/mois (tenant)',
    features: {
      'Bibliothèque de messages organisés': true,
      'Collaboration éditoriale': true,
      'Tâches et suivi': true,
      'Multi-tenant sécurisé natif': true,
      'Diffusion de messages structurée': true,
    },
    color: '#2563eb',
    isPulseCIO: true,
  },
];

const FEATURE_KEYS = [
  'Bibliothèque de messages organisés',
  'Collaboration éditoriale',
  'Tâches et suivi',
  'Multi-tenant sécurisé natif',
  'Diffusion de messages structurée',
];

const Comparison = () => {
  return (
    <section id="comparison" className="py-20 bg-gray-50 dark:bg-gray-900">
      <div className="container mx-auto px-6">
        <h2 className="text-3xl font-bold mb-4 text-center">Pourquoi remplacer 4 outils par PulseCIO ?</h2>
        <p className="text-gray-600 dark:text-gray-300 mb-8 text-center max-w-3xl mx-auto">
          Aujourd'hui, les organisations paient <strong>27€ à 50€/utilisateur/mois</strong> en empilant Notion, ClickUp, Confluence et des outils de com interne. 
          <br />
          <strong>Aucun ne fait tout</strong> — PulseCIO unifie communication structurée, gestion de tâches et collaboration pour tous.
        </p>

        {/* Desktop table view */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full border-collapse bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
            <thead>
              <tr className="bg-gray-100 dark:bg-gray-700">
                <th className="p-4 text-left font-semibold">Fonctionnalité</th>
                {TOOLS_COMPARISON.map((tool) => (
                  <th 
                    key={tool.tool} 
                    className={`p-4 text-center font-bold ${tool.isPulseCIO ? 'bg-blue-100 dark:bg-blue-900/40' : ''}`}
                  >
                    <div className="flex flex-col items-center gap-1">
                      <span style={{ color: tool.color }}>{tool.tool}</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400 font-normal">{tool.price}</span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {FEATURE_KEYS.map((feature, idx) => (
                <tr key={feature} className={idx % 2 === 0 ? 'bg-gray-50 dark:bg-gray-800/50' : ''}>
                  <td className="p-4 font-medium text-sm">{feature}</td>
                  {TOOLS_COMPARISON.map((tool) => (
                    <td 
                      key={tool.tool} 
                      className={`p-4 text-center ${tool.isPulseCIO ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
                    >
                      {tool.features[feature] ? (
                        <FiCheck className="inline text-green-600 dark:text-green-400 text-xl" />
                      ) : (
                        <FiX className="inline text-gray-300 dark:text-gray-600 text-xl" />
                      )}
                    </td>
                  ))}
                </tr>
              ))}
              <tr className="bg-blue-100 dark:bg-blue-900/30 font-bold">
                <td className="p-4 text-sm">Exemples d'économies réelles</td>
                <td className="p-4 text-center text-gray-500">-</td>
                <td className="p-4 text-center text-gray-500">-</td>
                <td className="p-4 text-center text-gray-500">-</td>
                <td className="p-4 text-center text-gray-500">-</td>
                <td className="p-4 text-center text-green-600 dark:text-green-400 text-xs">
                  <div className="space-y-1">
                    <div>10 users: ~262€/mois</div>
                    <div>30 users: ~795-1185€/mois</div>
                    <div>50 users: ~1325-2475€/mois</div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Mobile card view */}
        <div className="lg:hidden space-y-6">
          {TOOLS_COMPARISON.map((tool) => (
            <div 
              key={tool.tool}
              className={`p-6 rounded-lg border ${
                tool.isPulseCIO 
                  ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-600' 
                  : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
              }`}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold" style={{ color: tool.color }}>{tool.tool}</h3>
                <span className="text-sm text-gray-600 dark:text-gray-400">{tool.price}</span>
              </div>
              <ul className="space-y-2">
                {FEATURE_KEYS.map((feature) => (
                  <li key={feature} className="flex items-center gap-2 text-sm">
                    {tool.features[feature] ? (
                      <FiCheck className="text-green-600 dark:text-green-400 flex-shrink-0" />
                    ) : (
                      <FiX className="text-gray-300 dark:text-gray-600 flex-shrink-0" />
                    )}
                    <span className={tool.features[feature] ? '' : 'text-gray-400 dark:text-gray-600'}>
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>
              {tool.isPulseCIO && (
                <div className="mt-4 pt-4 border-t border-blue-200 dark:border-blue-700">
                  <p className="text-green-600 dark:text-green-400 font-semibold text-center text-xs">
                    💰 Économies réelles :<br/>
                    10 users: ~262€/mois<br/>
                    30 users: ~795-1185€/mois<br/>
                    50 users: ~1325-2475€/mois
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-8 p-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg">
          <h3 className="text-xl font-bold mb-2 text-green-800 dark:text-green-200">🎯 Le verdict</h3>
          <p className="text-gray-700 dark:text-gray-300">
            <strong>PulseCIO n'est pas un simple outil de messagerie</strong> — c'est une <strong>plateforme de communication structurée et de collaboration</strong>, adaptée aux DSI, CIO Office, PME, associations et toute organisation cherchant à gagner en efficacité.
            <br />
            Là où vous payez 4 à 6 outils qui ne se parlent pas, <strong>PulseCIO unifie tout</strong> pour moins cher, avec une expérience cohérente et un audit intégré.
          </p>
        </div>
      </div>
    </section>
  );
};

export default Comparison;
